import React, { useState, useEffect, useRef } from "react";
import { 
    Grid, 
    Card, 
    InputLabel, 
    Icon, 
    CircularProgress, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Button,
    Chip,
    Tooltip
} from "@mui/material";
import { toDateTime } from "utils/DateUtils";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSelect from "components/MDSelect";
import SeletorMarcacoes from "./SeletorMarcacoes";
import MDInput from "components/MDInput";
import MDSnackbar from "components/MDSnackbar";
import { get, post, postNoResponse } from "utils/WebService";
import DevolucaoDiretaDialog from "../DevolucaoDiretaDialog";
import { useMaterialUIController } from "context";
import { useUser } from "context/UserContext";
import { TIPO_PERFIL, podeDevolverOuMigrarDireto, temTipoPerfil } from "utils/Perfis";
import { ESCURO } from "assets/coresTema";

export default function DadosCarteira({
    recordId, 
    preloadedDevedor, 
    selectedFicha,
    onSelectFicha,
    selectedFichaInitial,
    isViewOnly = false,
    onDataUpdated
}) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const { userPerfils, userFunctionalities } = useUser();

    const isGestorAdminOuCoord = temTipoPerfil(userPerfils,
        TIPO_PERFIL.GESTOR, TIPO_PERFIL.COORDENADOR, TIPO_PERFIL.ADMINISTRADOR, TIPO_PERFIL.SUPORTE);

    // Devolve e reativa a ficha direto; sem a permissão, solicita a devolução para a Fila de Solicitações
    const isAdminOrSuporte = podeDevolverOuMigrarDireto(userPerfils, userFunctionalities);

    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [fichas, setFichas] = useState([]);
    const [selectedFichaIndex, setSelectedFichaIndex] = useState(0);

    const [formValues, setFormValues] = useState({
        statusId: "",
        usuarioId: "",
        marcacoesIds: [],
        observacao: ""
    });

    const [statusOptions, setStatusOptions] = useState([]);
    const [usuariosOptions, setUsuariosOptions] = useState([]);

    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);
    const [sbMessage, setSbMessage] = useState("");

    // Estados de Devolução
    const [devolucaoDireta, setDevolucaoDireta] = useState(null);
    const [confirmAction, setConfirmAction] = useState({ open: false, title: "", message: "", onConfirm: null });

    const loadOptions = () => {
        get("/status-ficha/list-all").then(res => {
            const list = res?.list || res?.data || (Array.isArray(res) ? res : []);
            setStatusOptions(list.sort((a, b) => String(a.ccNome || a.nome || "").localeCompare(String(b.ccNome || b.nome || ""))));
        }).catch(() => {});

        get("/users/list-all-operators").then(res => {
            setUsuariosOptions(res?.list || res?.data || (Array.isArray(res) ? res : []));
        }).catch(() => {});

    };

    const processDevedorData = (data) => {
        if (!data) return;
        let list = [];
        if (data.fichas && Array.isArray(data.fichas) && data.fichas.length > 0) {
            const valid = data.fichas.filter(f => {
                // Ficha fora das equipes do usuário (o backend já não manda; vale para qualquer perfil)
                if (f.pertenceAMinhaEquipe === false) return false;
                return true;
            });
            list = valid.length > 0 ? valid : data.fichas;
        }

        if (list.length === 0) {
            // Devedor sem ficha: só exibe. O id dele não pode ir como id de ficha (o backend acharia a ficha de outro devedor com o mesmo número)
            list = [{
                semFicha: true,
                rawId: null,
                id: null,
                nomeCarteira: data.nomeCarteira || "Carteira Principal",
                idStatusFicha: data.idStatusFicha || data.statusId || "",
                nomeStatusFicha: data.statusFichaNome || data.nomeStatusFicha || "",
                idUsuarioDono: data.idUsuario || data.idUsuarioDono || "",
                nomeUsuarioDono: data.nomeUsuario || data.nomeUsuarioDono || "",
                marcacoesIds: data.marcacoesIds || [],
                observacao: data.observacao || "",
                situacao: data.situacao,
                nomeCampanha: data.nomeCampanha,
                enquadramentoCampanha: data.enquadramentoCampanha,
                dataDevolucao: data.dataDevolucao,
                nomeUsuarioDevolucao: data.nomeUsuarioDevolucao
            }];
        }

        setFichas(list);
    };

    const loadDevedorFichas = () => {
        if (!recordId) return;
        setLoading(true);
        get(`/devedor/by-id/${recordId}`)
            .then(data => {
                processDevedorData(data);
            })
            .catch(err => console.error("Erro ao carregar fichas em DadosCarteira", err))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadOptions();
        if (preloadedDevedor) {
            processDevedorData(preloadedDevedor);
        }
        loadDevedorFichas();

        const handleReload = () => loadDevedorFichas();
        window.addEventListener("devedorStatusUpdated", handleReload);
        return () => window.removeEventListener("devedorStatusUpdated", handleReload);
    }, [recordId]);

    useEffect(() => {
        if (preloadedDevedor) {
            processDevedorData(preloadedDevedor);
        }
    }, [preloadedDevedor]);

    useEffect(() => {
        const target = selectedFicha || selectedFichaInitial;
        if (target && fichas.length > 0) {
            const idx = fichas.findIndex(f => 
                (f.rawId && target.rawId && String(f.rawId) === String(target.rawId)) ||
                (f.id && target.id && f.id === target.id)
            );
            if (idx >= 0 && idx !== selectedFichaIndex) {
                setSelectedFichaIndex(idx);
            }
        }
    }, [selectedFicha, selectedFichaInitial, fichas]);

    const activeFicha = fichas.length > 0 ? fichas[selectedFichaIndex] || fichas[0] : null;
    const chaveFichaAtiva = activeFicha ? String(activeFicha.rawId || activeFicha.id || "") : "";

    // O usuário alterou o formulário e ainda não salvou: recargas da mesma ficha (a lista chega mais de uma vez ao abrir,
    // outra aba avisa que a ficha mudou) não podem apagar o que ele digitou
    const editado = useRef(false);
    const fichaDoFormulario = useRef(null);
    const alterarFormulario = (mudanca) => {
        editado.current = true;
        setFormValues(prev => ({ ...prev, ...mudanca }));
    };

    useEffect(() => {
        if (!activeFicha) return;
        if (editado.current && fichaDoFormulario.current === chaveFichaAtiva) return;
        editado.current = false;
        fichaDoFormulario.current = chaveFichaAtiva;
        setFormValues({
            statusId: activeFicha.idStatusFicha || activeFicha.statusId || "",
            usuarioId: activeFicha.idUsuarioDono || activeFicha.idUsuario || "",
            marcacoesIds: activeFicha.marcacoesIds || [],
            observacao: activeFicha.observacao || ""
        });
    }, [activeFicha, chaveFichaAtiva]);

    const handleSave = () => {
        if (!activeFicha) return;
        if (activeFicha.semFicha) {
            setSbMessage("Este devedor não tem ficha em nenhuma carteira: não há onde gravar esses dados.");
            setErrorSB(true);
            return;
        }
        setIsSaving(true);
        const payload = {
            carteiraId: activeFicha.idCarteira || activeFicha.carteiraId || null,
            statusId: formValues.statusId || null,
            usuarioId: formValues.usuarioId || null,
            marcacoesIds: formValues.marcacoesIds || [],
            observacao: formValues.observacao || ""
        };

        const targetId = activeFicha.rawId || activeFicha.id;
        postNoResponse(`/devedor/carteira/update/${targetId}`, payload)
            .then(() => {
                editado.current = false;
                setSbMessage("Dados da carteira salvos com sucesso!");
                setSuccessSB(true);
                loadDevedorFichas();
                window.dispatchEvent(new Event("devedorStatusUpdated"));
                if (onDataUpdated) onDataUpdated();
            })
            .catch(err => {
                console.error("Erro ao salvar dados da carteira", err);
                setSbMessage(err?.message ? `Erro ao salvar os dados da carteira: ${err.message}` : "Erro ao salvar os dados da carteira.");
                setErrorSB(true);
            })
            .finally(() => setIsSaving(false));
    };

    // Pergunta "deseja devolver?" e depois a tela de texto/anexo que vão junto da ocorrência (DevolucaoDiretaDialog)
    const handleDevolverDireto = () => {
        const targetId = activeFicha?.rawId || activeFicha?.id || recordId;
        const fichaDesc = activeFicha?.rawId ? `Ficha #${activeFicha.rawId}` : (activeFicha?.nomeCarteira || "desta carteira");
        setDevolucaoDireta({
            titulo: "Devolver Ficha",
            pergunta: `Deseja realmente devolver a ${fichaDesc}?`,
            endpoint: `/devedor/devolver/${targetId}`,
            idDevedor: recordId,
        });
    };

    const aoConcluirDevolucaoDireta = (avisoAnexo) => {
        setSbMessage(`Ficha devolvida com sucesso!${avisoAnexo}`);
        if (avisoAnexo) setErrorSB(true); else setSuccessSB(true);
        loadDevedorFichas();
        window.dispatchEvent(new Event("devedorStatusUpdated"));
        if (onDataUpdated) onDataUpdated();
    };

    const handleReativar = () => {
        const targetId = activeFicha?.rawId || activeFicha?.id || recordId;
        const fichaDesc = activeFicha?.rawId ? `Ficha #${activeFicha.rawId}` : (activeFicha?.nomeCarteira || "desta carteira");
        setConfirmAction({
            open: true,
            title: "Reativar Ficha",
            message: `Deseja realmente reativar a ${fichaDesc}?`,
            onConfirm: async () => {
                try {
                    await postNoResponse(`/devedor/reativar/${targetId}`, {});
                    setSbMessage("Ficha reativada com sucesso!");
                    setSuccessSB(true);
                    loadDevedorFichas();
                    window.dispatchEvent(new Event("devedorStatusUpdated"));
                    if (onDataUpdated) onDataUpdated();
                } catch (e) {
                    setSbMessage("Erro ao reativar ficha.");
                    setErrorSB(true);
                }
            }
        });
    };

    const isFichaDevolvida = activeFicha?.situacao === 1;

    return (
        <MDBox>
            <Card sx={{ 
                p: 2, 
                backgroundColor: darkMode ? ESCURO.cartao : "#ffffff", 
                border: `1px solid ${isFichaDevolvida ? "#ef5350" : (darkMode ? "rgba(255,255,255,0.12)" : "#e0e0e0")}`,
                boxShadow: "none"
            }}>
                {loading ? (
                    <MDBox display="flex" justifyContent="center" py={4}>
                        <CircularProgress size={30} />
                    </MDBox>
                ) : (
                    <Grid container spacing={2.5}>

                        {/* Se a ficha estiver devolvida e houver apenas 1 ficha, mostra o aviso de Devolvida de forma compacta */}
                        {fichas.length <= 1 && isFichaDevolvida && (
                            <Grid item xs={12}>
                                <Chip 
                                    label={
                                        activeFicha?.dataDevolucao 
                                            ? `FICHA DEVOLVIDA EM ${toDateTime(activeFicha.dataDevolucao)}${activeFicha?.nomeUsuarioDevolucao ? ` (${activeFicha.nomeUsuarioDevolucao})` : ""}` 
                                            : "FICHA DEVOLVIDA"
                                    } 
                                    color="error" 
                                    size="small" 
                                    sx={{ fontWeight: "bold", fontSize: "10px" }} 
                                />
                            </Grid>
                        )}

                        {/* Propriedade (Responsável) */}
                        <Grid item xs={12} sm={4}>
                            <InputLabel sx={{ px: 1, pb: 0.5 }}>
                                Propriedade (Responsável)
                            </InputLabel>
                            <MDSelect
                                isMulti={false}
                                getOptionValue={(option) => option.id}
                                getOptionLabel={(option) => option.nome}
                                placeholder="Selecione o responsável..."
                                value={usuariosOptions.find(u => String(u.id) === String(formValues.usuarioId)) || (activeFicha?.nomeUsuarioDono || activeFicha?.nomeUsuario ? { id: activeFicha.idUsuarioDono || activeFicha.idUsuario, nome: activeFicha.nomeUsuarioDono || activeFicha.nomeUsuario } : null)}
                                options={usuariosOptions}
                                onChange={(val) => alterarFormulario({ usuarioId: val ? val.id : "" })}
                                isDisabled={!isGestorAdminOuCoord || isViewOnly}
                            />
                        </Grid>

                        {/* Status da Ficha */}
                        <Grid item xs={12} sm={4}>
                            <InputLabel sx={{ px: 1, pb: 0.5 }}>
                                Status da Ficha
                            </InputLabel>
                            <MDSelect
                                isMulti={false}
                                getOptionValue={(option) => option.id}
                                getOptionLabel={(option) => option.ccNome || option.nome}
                                placeholder="Selecione o status..."
                                value={statusOptions.find(s => String(s.id) === String(formValues.statusId)) || (activeFicha?.nomeStatusFicha || activeFicha?.statusFichaNome ? { id: activeFicha.idStatusFicha, ccNome: activeFicha.nomeStatusFicha || activeFicha.statusFichaNome } : null)}
                                options={statusOptions}
                                onChange={(val) => alterarFormulario({ statusId: val ? val.id : "" })}
                                isDisabled={isViewOnly}
                            />
                        </Grid>

                        {/* Marcação Especial */}
                        <Grid item xs={12} sm={4}>
                            <InputLabel sx={{ px: 1, pb: 0.5 }}>
                                Marcação Especial
                            </InputLabel>
                            <SeletorMarcacoes
                                carteira={activeFicha?.idCarteira || activeFicha?.carteiraId}
                                alvos={[1, 3]}
                                value={formValues.marcacoesIds}
                                onChange={(ids) => alterarFormulario({ marcacoesIds: ids })}
                                isDisabled={isViewOnly}
                            />
                        </Grid>

                        {/* Cód. Contrato: código do cliente que não é a chave do devedor (vem da remessa ou do sistema antigo) */}
                        {activeFicha?.codContrato && (
                            <Grid item xs={12} sm={4}>
                                <InputLabel sx={{ px: 1, pb: 0.5 }}>
                                    Cód. Contrato
                                </InputLabel>
                                <MDInput fullWidth size="small" value={activeFicha.codContrato} inputProps={{ readOnly: true }} />
                                <MDTypography variant="caption" color="text" sx={{ px: 1, display: "block" }}>
                                    Código do cliente na remessa ou no sistema antigo (não é o Código Cliente).
                                </MDTypography>
                            </Grid>
                        )}

                        {/* Observação da Carteira */}
                        <Grid item xs={12}>
                            <InputLabel sx={{ px: 1, pb: 0.5 }}>
                                Observação da Carteira
                            </InputLabel>
                            <MDInput
                                fullWidth
                                multiline
                                rows={4}
                                placeholder="Observações operacionais específicas desta carteira..."
                                value={formValues.observacao}
                                onChange={(e) => alterarFormulario({ observacao: e.target.value })}
                                inputProps={{
                                    readOnly: isViewOnly
                                }}
                            />
                        </Grid>

                        {/* Ações: Salvar e Devolução */}
                        <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mt={1}>
                            <MDBox display="flex" gap={1.5} alignItems="center">
                                {isFichaDevolvida && isAdminOrSuporte && (
                                    <MDButton 
                                        variant="gradient" 
                                        color="success" 
                                        onClick={handleReativar}
                                        disabled={isSaving}
                                    >
                                        <Icon sx={{ mr: 0.5 }}>restore</Icon> Reativar Ficha
                                    </MDButton>
                                )}

                                {/* Pedir a devolução: pelo "Pedir ao suporte" da ficha; aqui só a devolução direta */}
                                {!isFichaDevolvida && isAdminOrSuporte && (
                                    <MDButton variant="gradient" color="error" onClick={handleDevolverDireto} disabled={isSaving}>
                                        <Icon sx={{ mr: 0.5 }}>assignment_return</Icon> Devolver Ficha
                                    </MDButton>
                                )}
                            </MDBox>

                            <MDButton 
                                variant="gradient" 
                                color="info" 
                                onClick={handleSave} 
                                disabled={isSaving || isViewOnly}
                            >
                                {isSaving ? <CircularProgress size={18} color="inherit" /> : "Salvar Dados da Carteira"}
                            </MDButton>
                        </Grid>
                    </Grid>
                )}
            </Card>

            <DevolucaoDiretaDialog alvo={devolucaoDireta} onClose={() => setDevolucaoDireta(null)} onConcluida={aoConcluirDevolucaoDireta} />

            {/* Diálogo de Confirmação */}
            <Dialog 
                open={confirmAction.open}
                onClose={() => setConfirmAction({ open: false, title: "", message: "", onConfirm: null })}
                PaperProps={{
                    sx: {
                        background: darkMode ? ESCURO.cartao : "#fff",
                        color: darkMode ? "#fff" : "inherit"
                    }
                }}
            >
                <DialogTitle>{confirmAction.title}</DialogTitle>
                <DialogContent>
                    <MDTypography variant="body2" color={darkMode ? "white" : "text"}>
                        {confirmAction.message}
                    </MDTypography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmAction({ open: false, title: "", message: "", onConfirm: null })} color="inherit">
                        Cancelar
                    </Button>
                    <Button 
                        variant="contained" 
                        color="primary"
                        onClick={async () => {
                            const fn = confirmAction.onConfirm;
                            setConfirmAction({ open: false, title: "", message: "", onConfirm: null });
                            if (fn) await fn();
                        }}
                    >
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>

            <MDSnackbar
                color="success"
                icon="check"
                title="Sucesso"
                content={sbMessage}
                dateTime=""
                open={successSB}
                onClose={() => setSuccessSB(false)}
                close={() => setSuccessSB(false)}
                bgWhite
            />
            <MDSnackbar
                color="error"
                icon="warning"
                title="Erro"
                content={sbMessage}
                dateTime=""
                open={errorSB}
                onClose={() => setErrorSB(false)}
                close={() => setErrorSB(false)}
                bgWhite
            />
        </MDBox>
    );
}
